import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, Bell, Clock, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/useMobileUtils";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";

const NotificationSetting = ({
  title,
  description,
  checked,
  onCheckedChange,
}) => (
  <div className="flex items-center justify-between py-4 gap-4">
    <div className="space-y-0.5 flex-1 min-w-0">
      <p className="font-medium">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </div>
    <Switch checked={checked} onCheckedChange={onCheckedChange} />
  </div>
);

export const Notifications = () => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuthStore();
  const isMobile = useIsMobile();

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    signatureReminders: true,
    marketingEmails: false,
    completionAlerts: true,
    weeklyDigest: false,
  });

  useEffect(() => {
    if (user?.settings?.notifications) {
      setNotifications((prev) => ({ ...prev, ...user.settings.notifications }));
    }
  }, [user]);

  const updateNotificationsMutation = useMutation({
    mutationFn: (notificationSettings) => {
      const currentSettings = user?.settings || {};
      return authApi.updateProfile({
        settings: { ...currentSettings, notifications: notificationSettings },
      });
    },
    onSuccess: (response) => {
      const userData = response.data?.data;
      if (userData) {
        updateUser(userData);
      }
      toast.success(t("settings.notifications.saved"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.error?.message ||
          t("settings.notifications.saveFailed"),
      );
    },
  });

  const loading = updateNotificationsMutation.isPending;

  const handleNotificationsUpdate = (e) => {
    e.preventDefault();
    updateNotificationsMutation.mutate(notifications);
  };

  return (
    <div className="space-y-6 pb-20 sm:pb-0">
      <div className="flex items-center gap-4">
        <Link
          to="/settings"
          className="flex items-center justify-center h-10 w-10 sm:h-8 sm:w-8 rounded-lg border hover:bg-accent touch-target"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("settings.notifications.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("settings.notifications.description")}
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            {t("settings.notifications.emailTitle")}
          </CardTitle>
          <CardDescription>
            {t("settings.notifications.emailDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleNotificationsUpdate}>
            <div className="divide-y">
              <NotificationSetting
                title={t("settings.notifications.emailNotifications")}
                description={t("settings.notifications.documentSignedDesc")}
                checked={notifications.emailNotifications}
                onCheckedChange={(checked) =>
                  setNotifications((n) => ({
                    ...n,
                    emailNotifications: checked,
                  }))
                }
              />
              <NotificationSetting
                title={t("settings.notifications.documentExpiring")}
                description={t("settings.notifications.documentExpiringDesc")}
                checked={notifications.signatureReminders}
                onCheckedChange={(checked) =>
                  setNotifications((n) => ({
                    ...n,
                    signatureReminders: checked,
                  }))
                }
              />
              <NotificationSetting
                title={t("settings.notifications.documentCompleted")}
                description={t("settings.notifications.documentCompletedDesc")}
                checked={notifications.completionAlerts}
                onCheckedChange={(checked) =>
                  setNotifications((n) => ({ ...n, completionAlerts: checked }))
                }
              />
              <NotificationSetting
                title={t("settings.notifications.weeklyDigest")}
                description={t("settings.notifications.weeklyDigestDesc")}
                checked={notifications.weeklyDigest}
                onCheckedChange={(checked) =>
                  setNotifications((n) => ({ ...n, weeklyDigest: checked }))
                }
              />
              <NotificationSetting
                title={t("settings.notifications.productUpdates")}
                description={t("settings.notifications.productUpdatesDesc")}
                checked={notifications.marketingEmails}
                onCheckedChange={(checked) =>
                  setNotifications((n) => ({ ...n, marketingEmails: checked }))
                }
              />
            </div>

            <div className="pt-6">
              <Button
                type="submit"
                disabled={loading}
                size={isMobile ? "mobile" : "default"}
                className={isMobile ? "w-full" : ""}
              >
                {loading ? t("common.saving") : t("common.saveChanges")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t("settings.notifications.advancedReminders")}
          </CardTitle>
          <CardDescription>
            {t("settings.notifications.advancedRemindersDesc")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Set up customizable reminder schedules with interval-based or
            fixed-day options, configure escalation emails for unresponsive
            signers, and enable SMS reminders.
          </p>
          <Button
            variant="outline"
            asChild
            size={isMobile ? "mobile" : "default"}
            className={isMobile ? "w-full" : ""}
          >
            <Link
              to="/settings/reminders"
              className="inline-flex items-center gap-2"
            >
              Configure Default Reminders
              <ChevronRight className="h-4 w-4" />
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
